import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useSelector } from 'react-redux';
import { UploadArea } from 'src/components/atlas/molecules';
import { handleErrorToast } from 'src/components/molecules';
import {
  InvestigationPlan,
  WorkspaceInvestigation,
} from 'src/components/organisms/Investigation';
import { MitigationPlanSection } from 'src/components/organisms/MitigationPlan/MitigationPlanSection';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import {
  CaseByIdQuery,
  CaseDocumentationType,
  MitigationPlanFragment,
} from 'src/graphql';
import { UseCaseDocuments } from 'src/hooks/cases/useCaseDocuments';
import { CaseTypeEnum } from 'src/types';
import {
  formatValidationError,
  validateFileUpload,
} from 'src/utils/validateFileUpload/fileValidation';
import { CaseWorkspaceTable } from './CaseWorkspaceTable';
import { WorkspaceDocumentEditor } from './WorkspaceDocumentEditor';

interface Props {
  caseId: string;
  userMap: Record<number, { name: string; role?: string }>;
  isSaving: boolean;
  setIsSaving: (v: boolean) => void;
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>;
  documents: CaseDocumentationType[];
  caseDocuments: UseCaseDocuments;
  caseType: CaseTypeEnum;
  investigationPlan?: InvestigationPlan | null;
  onInvestigationChecklistUpdate?: (updatedMarkdown: string) => void;
  onAddMitigationPlan?: () => void;
  onEditMitigationPlan?: (plan: MitigationPlanFragment) => void;
  caseStatus: string;
}

const handleFileProcessing = async (
  files: File[],
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >,
  setInvalidFileNames: (fileNames: string[]) => void,
  setIsUploading: (uploading: boolean) => void,
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>,
) => {
  const validFiles: File[] = [];
  const invalidFiles: string[] = [];

  for (const file of files) {
    try {
      validateFileUpload({
        filename: file.name,
        mimeType: file.type,
      });
      validFiles.push(file);
    } catch (error) {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    setInvalidFileNames(invalidFiles);
  }

  if (validFiles.length > 0) {
    setIsUploading(true);
    let anyUploadSucceeded = false;
    try {
      for (const file of validFiles) {
        await caseDocuments.updateFile(
          file.name,
          file,
          {},
          () => {
            anyUploadSucceeded = true;
          },
          (errorText: string) => {
            console.error(`Failed to upload file '${file.name}':`, errorText);
          },
          () => {}, // progress
          () => {}, // abort
          (_transferId: string) => {}, // transfer
          {
            // options
            chunkTransferId: '',
            chunkServer: { url: '' },
            chunkUploads: false,
            chunkForce: false,
            chunkSize: 0,
            chunkRetryDelays: [],
          },
        );
      }
    } catch (error) {
      handleErrorToast({
        toastMessage: 'Upload failed',
        toastDescription:
          'Something went wrong while uploading. Please try again.',
      });
    } finally {
      setIsUploading(false);
    }
    // Only refetch when at least one upload succeeded
    if (anyUploadSucceeded) {
      await refetch();
    }
  }
};

export const CaseWorkspace: FC<Props> = ({
  caseId,
  userMap,
  isSaving,
  setIsSaving,
  refetch,
  documents,
  caseDocuments,
  caseType,
  investigationPlan,
  onInvestigationChecklistUpdate,
  onAddMitigationPlan,
  onEditMitigationPlan,
  caseStatus,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const [isUploading, setIsUploading] = useState(false);
  const [invalidFileNames, setInvalidFileNames] = useState<string[]>([]);
  const [isWorkspaceDocumentEditorOpen, setIsWorkspaceDocumentEditorOpen] =
    useState(false);
  const [workspaceReadOnly, setWorkspaceReadOnly] = useState(false);

  const reduxCase = useSelector(casePageVm);

  useEffect(() => {
    if (invalidFileNames.length > 0) {
      handleErrorToast({
        toastMessage: 'Unsupported file type',
        toastDescription: formatValidationError(invalidFileNames),
      });
      setInvalidFileNames([]);
    }
  }, [invalidFileNames]);

  const userHasEditAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  const handleWorkspaceDocumentEditorClosed = () => {
    setIsWorkspaceDocumentEditorOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: sp[4].value,
        }}
      >
        {userHasEditAccess &&
          investigationPlan &&
          onInvestigationChecklistUpdate && (
            <WorkspaceInvestigation
              caseId={caseId}
              plan={investigationPlan}
              onChecklistUpdate={onInvestigationChecklistUpdate}
            />
          )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: sp[2].value,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="h3"
                fontWeight={tokens.semantic.fontWeight.emphasis.value}
                color={color.type.default.value}
                pb={sp[1].value}
              >
                Documents
              </Typography>
              <Typography variant="body1" color={color.type.muted.value}>
                You can upload any additional documentation to this report. This
                document will not be visible to the submitter.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setWorkspaceReadOnly(false);
                setIsWorkspaceDocumentEditorOpen(true);
              }}
              startIcon={<AddIcon size="md" />}
              data-testid="add-document-button"
              disabled={!userHasEditAccess}
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                borderRadius: tokens.semantic.radius.lg.value,
                borderColor: color.type.default.value,
                color: color.type.default.value,
                '&:hover': {
                  borderColor: color.type.default.value,
                  backgroundColor: 'transparent',
                },
              }}
            >
              Add document
            </Button>
          </Box>
          {userHasEditAccess && (
            <UploadArea
              variant="compact"
              isUploading={isUploading}
              onFilesSelected={(files) =>
                handleFileProcessing(
                  files,
                  caseDocuments,
                  setInvalidFileNames,
                  setIsUploading,
                  refetch,
                )
              }
            />
          )}
          <CaseWorkspaceTable
            caseDocuments={caseDocuments}
            documents={documents}
            selectedDocuments={caseDocuments.selectedDocuments ?? []}
            setSelectedDocuments={caseDocuments.setSelectedDocuments}
            openDocument={caseDocuments.handleOpenDocument}
            onOpenEditor={() => setIsWorkspaceDocumentEditorOpen(true)}
            setWorkspaceReadOnly={setWorkspaceReadOnly}
          />
        </Box>

        {caseType === CaseTypeEnum.COI && (
          <MitigationPlanSection
            caseId={caseId}
            userMap={userMap}
            canAdd={userHasEditAccess}
            onAddClick={onAddMitigationPlan}
            onEditClick={onEditMitigationPlan}
            onPlanRecalled={refetch}
            onPlanUpdated={refetch}
            caseStatus={caseStatus}
          />
        )}
      </Box>

      <WorkspaceDocumentEditor
        caseId={caseId}
        caseDocuments={caseDocuments}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        refetch={refetch}
        open={isWorkspaceDocumentEditorOpen}
        onClosed={handleWorkspaceDocumentEditorClosed}
        isReadOnly={!userHasEditAccess || workspaceReadOnly}
      />
    </>
  );
};
